import { useState } from "react";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { AlertTriangle } from "lucide-react";
import { AvatarStack } from "@/components/ui/avatar-stack";
import { Skeleton } from "@/components/ui/skeleton";
import { StageChip } from "@/components/tasks/stage-ui";
import type { PublicStage, PublicTask } from "@/services/public-view.service";
import { PublicRoadmapSummary } from "./PublicRoadmapSummary";
import { FALLBACK_STAGE_COLOR, publicAssignees } from "./utils";
import {
	ROADMAP_BUCKETS,
	friendlyStage,
	groupByBucket,
	type RoadmapBucket,
} from "./roadmap";

const PAGE_SIZE = 15;

function RoadmapCard({
	task,
	bucket,
	stage,
}: {
	task: PublicTask;
	bucket: RoadmapBucket;
	stage?: PublicStage;
}) {
	const assignees = publicAssignees(task);

	return (
		<div className="flex flex-col gap-2 rounded-sm border border-border bg-surface p-3 shadow-xs">
			<div className="flex flex-wrap items-center gap-1.5">
				<p className="text-[11px] text-muted">{task.project.name}</p>
				{task.priority === "urgent" && bucket !== "done" && (
					<span className="ml-auto inline-flex items-center gap-1 rounded bg-danger-subtle px-1.5 py-0.5 text-[10px] font-medium text-danger">
						<AlertTriangle className="h-3 w-3" />
						Urgent
					</span>
				)}
			</div>
			<p className="text-sm font-medium leading-snug text-foreground">{task.title}</p>
			{bucket === "now" && (
				<div>
					<StageChip
						label={friendlyStage(task.status, stage)}
						color={stage?.color ?? FALLBACK_STAGE_COLOR}
					/>
				</div>
			)}
			<div className="flex items-center justify-between gap-2 border-t border-border pt-2 text-[11px] text-muted-foreground">
				{assignees.length > 0 ? (
					<div className="flex min-w-0 items-center gap-1.5">
						<AvatarStack profiles={assignees} size="sm" className="shrink-0" />
						<span className="truncate">
							{assignees.map((a) => a.full_name?.split(" ")[0] ?? "Unnamed").join(", ")}
						</span>
					</div>
				) : (
					<span className="text-muted">Not assigned yet</span>
				)}
				<span className="shrink-0 text-muted">
					{bucket !== "done" && task.due_date
						? `Due ${format(parseISO(task.due_date), "MMM d")}`
						: `${bucket === "done" ? "Finished" : "Updated"} ${formatDistanceToNow(new Date(task.updated_at), { addSuffix: true })}`}
				</span>
			</div>
		</div>
	);
}

function BucketColumn({
	bucket,
	title,
	hint,
	tasks,
	stageByKey,
}: {
	bucket: RoadmapBucket;
	title: string;
	hint: string;
	tasks: PublicTask[];
	stageByKey: Map<string, PublicStage>;
}) {
	const [shown, setShown] = useState(PAGE_SIZE);

	return (
		<section aria-label={title} className="flex flex-col gap-2.5 rounded bg-muted-subtle p-3">
			<header className="px-1 pb-1">
				<div className="flex items-center gap-2">
					<h2 className="text-sm font-semibold text-foreground">{title}</h2>
					<span className="ml-auto rounded-full bg-surface px-2 py-0.5 text-xs font-medium text-muted">
						{tasks.length}
					</span>
				</div>
				<p className="text-xs text-muted">{hint}</p>
			</header>

			{tasks.length === 0 ? (
				<p className="px-1 py-6 text-center text-xs text-muted">Nothing here right now.</p>
			) : (
				tasks.slice(0, shown).map((task) => (
					<RoadmapCard
						key={task.id}
						task={task}
						bucket={bucket}
						stage={stageByKey.get(task.status)}
					/>
				))
			)}

			{tasks.length > shown && (
				<button
					type="button"
					onClick={() => setShown((n) => n + PAGE_SIZE)}
					className="rounded py-2 text-xs font-medium text-primary hover:bg-surface"
				>
					Show more ({tasks.length - shown} left)
				</button>
			)}
		</section>
	);
}

export function PublicTaskRoadmap({
	tasks,
	stages,
	loading,
}: {
	tasks: PublicTask[];
	stages: PublicStage[];
	loading: boolean;
}) {
	const groups = groupByBucket(tasks);
	const counts = { now: groups.now.length, next: groups.next.length, done: groups.done.length };
	const stageByKey = new Map(stages.map((s) => [s.key, s]));

	return (
		<div>
			<PublicRoadmapSummary counts={counts} loading={loading} />
			{loading ? (
				<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
					{ROADMAP_BUCKETS.map((b) => (
						<Skeleton key={b.key} className="h-96 rounded" />
					))}
				</div>
			) : (
				<div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
					{ROADMAP_BUCKETS.map((b) => (
						<BucketColumn
							key={b.key}
							bucket={b.key}
							title={b.title}
							hint={b.hint}
							tasks={groups[b.key]}
							stageByKey={stageByKey}
						/>
					))}
				</div>
			)}
		</div>
	);
}
